import { React, useState, useMemo, useContext, useRef } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import moment from "moment";
import { routeNames } from "../../routeSegments";
import OverflowToolTip from "../shared/OverflowToolTip";
import Paginate from "../Paginate";
import Constants from "../../Constants";
import { usePagination, useSortBy, useTable } from "react-table";
import SortAscendingIcon from "mdi-react/SortAscendingIcon";
import SortDescendingIcon from "mdi-react/SortDescendingIcon";
import SortIcon from "mdi-react/SortIcon";
import LockIcon from "mdi-react/LockIcon";
import ConfirmPopup from "../../Utils/ConfirmPopup";
import { TableDataContext } from "../../Contexts/TableDataContext";
import { UserContext } from "../../Contexts/UserContext";
import { Tooltip } from "antd";
import { RaiseRequestContext } from "../../Contexts/RaiseRequestContext";
import useModule from "../../hooks/useModule";
import { fundInvestmentsExport } from "../../Config/config";
import ExcelDownloader from "../shared/ExcelDownloader";

const FundInvestments = ({ investmentsData, data }) => {

  const scrollRef = useRef(null);
  const { isVC } = useModule();
  const {isTrialExport, isStudent } = useContext(UserContext);
  const { forExMonthlyRate } = useContext(TableDataContext);
  const location = useLocation();
  const [alert, setalert] = useState({ message: "", show: false });
  const { isDirectoryUser } = useContext(UserContext);

  const onConfirmation = (flag) => {
    setalert({ show: false, message: "" });
  };

  const onBlurColumnClick = (cell) => {
    setalert({
      show: true,
      message: Constants.peSubscriptionMsg,
      isAlertOnly: true,
      closeBtnName: "OK",
    });
  };

  const customStringSortMemo = useMemo(() => {
    return (rowA, rowB, columnId, desc) => {
      const defaultVal = desc ? "--" : "A";
      return (rowA?.values[columnId]?.roundName ?? defaultVal).localeCompare(
        rowB?.values[columnId]?.roundName ?? defaultVal
      );
    };
  }, []);

  const dealDateSort = useMemo(() => {
    return (rowA, rowB, columnId, desc) => {
      return moment
        .utc(rowA?.values[columnId].date)
        .diff(moment.utc(rowB?.values[columnId].date));
    };
  }, []);

  const getForExRate = (dealDate) => {
    const dealMoment = moment(dealDate, "MM-YYYY").format("MM-YYYY");
    let rate = parseFloat(forExMonthlyRate?.[0]?.value); // Default to the earliest rate if no match found

    forExMonthlyRate?.forEach((forex) => {
      const forexMoment = moment(forex.date, "YYYY-MM-DD").format("MM-YYYY");
      if (forexMoment === dealMoment) {
        rate = parseFloat(forex.value);
      }
    });

    return rate;
  };

  const isValidNumber = (value) => {
    return typeof value === "number" && Number.isFinite(value);
  };

  const humanReadableAmountUsd = (a) => {
    if (!a) {
      return "";
    }

    if (typeof a === "string") {
      a = parseFloat(a.replace(",", ""));
    }
    if (!isValidNumber(a)) {
      return "";
    }
    const usdValueStr = a.toString();

    return `$ ${usdValueStr} M`;
  };

  const calculatePostMoneyUsd = (
    companyValuationPostMoney,
    date,
    amountInr,
    amount
  ) => {
    if (!companyValuationPostMoney) {
      return "";
    }

    const exRate =
      amountInr > 0 && amount > 0
        ? parseFloat(amountInr) / parseFloat(amount)
        : getForExRate(date) / 10;
    const usdValue = companyValuationPostMoney / exRate;
    const usdValueStr = humanReadableAmountUsd(usdValue.toFixed(2));
    if (!usdValueStr) {
      return "";
    }
    return `(${usdValueStr})`; // Converting and formatting to 2 decimal places
  };

  const columns = useMemo(() => {
    let blurColumns;

    const allowedModulesStr = localStorage.getItem(
      Constants.localStorageKeys.allowedModules
    );
    const allowedModulesList = JSON.parse(allowedModulesStr) ?? [];

    if (allowedModulesList.includes("pe") && !isDirectoryUser) {
      blurColumns = false;
    } else {
      blurColumns = true;
    }

    const addCommasToNumber = (number) => {
      if (!number) return "";
      return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    };

    return [
      {
        id: "company_name",
        Header: "Company Name",
        accessor: "company_name",
        className:
          "clickable w-[15%] text-left sticky left-0 lg:relative bg-white",
        headerspanClassName: "",
        headerClassName:
          "text-left sticky left-0 lg:relative  w-[3rem] bg-[#EDEDED] ",
        Cell: (cell) => {
          if (cell.value == Constants.restrictedPlaceholder) {
            return cell.value;
          }
          return (
            <OverflowToolTip
              className="w-[9rem] lg:w-auto"
              component={
                <Link
                  className="hover:text-[#9d6c01]"
                  onClick={(e) => {
                    if (
                      cell.value.restricted == Constants.restrictedPlaceholder
                    ) {
                      e.preventDefault();
                      e.stopPropagation();
                      onBlurColumnClick();
                    }
                  }}
                  rel="noopener noreferrer"
                  state={{ prevPath: location.state?.prevPath ?? "" }}
                  to={isVC ? routeNames.vcCompanyFundById(cell.value.id) : routeNames.peCompanyFundById(cell.value.id)}
                >
                  {cell.value.roundName ?? "--"}
                </Link>
              }
            />
          );
        },
        sortType: customStringSortMemo,
      },

      {
        id: "dealDate",
        Header: "Deal Date",
        accessor: "dealDate",
        className: "w-[5%]",
        headerspanClassName: "",
        headerClassName: "",
        Cell: (cell) => {
          const date = cell?.value?.date
            ? moment(cell.value.date).format("MMM YYYY")
            : ""; // Check if date is not null

          if (cell.value == Constants.restrictedPlaceholder) {
            return cell.value;
          }
          return (
            <Link
              className="hover:text-[#9d6c01]"
              target="_blank"
              onClick={(e) => {
                if (cell.value.restricted == Constants.restrictedPlaceholder) {
                  e.preventDefault();
                  e.stopPropagation();
                  onBlurColumnClick();
                }
              }}
              state={{ prevPath: location.state?.prevPath ?? "" }}
              to={(isVC ? routeNames.vcCompanyInvestmentDealById : routeNames.peCompanyInvestmentDealById)(
                cell.value.companyId,
                cell.value.id
              )}
            >
              {date}
              {cell.value.agghide && (
                <Tooltip
                  placement="right"
                  color="#ffffff"
                  key="#ffffff"
                  overlayStyle={{ maxWidth: "30%" }}
                  title={
                    <span style={{ color: "#000000" }}>
                      Not Included for Aggregate calculation as it does not meet
                      the Venture Intelligence definition of PE
                    </span>
                  }
                >
                  <span className="rounded-md bg-[#f3e8ff] px-2 py-[3px] ml-2 text-xs font-sans_book_body text-[#333333]">
                    NIA
                  </span>
                </Tooltip>
              )}
              {cell.value.debt && (
                <span className="rounded-md bg-[#f3e8ff] px-2  py-[3px] ml-2 text-xs  font-sans_book_body text-[#333333]">
                  Debt
                </span>
              )}
            </Link>
          );
        },
        sortType: dealDateSort,
      },
      {
        Header: "Inv. Amount (US$ M)",
        accessor: "dealAmountInUS",
        className:
          "text-right  w-[10%] pr-[2.7rem] md:pr-[2.7rem] 2xl:pr-[2.4rem] lg:pr-[2.75rem]",
        headerspanClassName: "ml-auto ",
        headerClassName: "",
        Cell: (cell) => {
          return addCommasToNumber(cell?.value);
        },
      },
      {
        Header: "Inv. Amount (INR Cr)",
        accessor: "dealAmountInCr",
        className:
          "text-right  w-[10%] pr-[2.7rem] md:pr-[2.7rem] 2xl:pr-[2.4rem] lg:pr-[2.75rem]",
        headerspanClassName: "ml-auto ",
        headerClassName: "",
        Cell: (cell) => {
          return addCommasToNumber(cell?.value);
        },
      },
      {
        Header: "Post Money Valuation",
        accessor: "postMoneyValuation",
        className: `text-right w-[10%]  pr-[2.7rem]`,
        headerspanClassName: "ml-auto ",
        headerClassName: "",
        disableSortBy: blurColumns,
        blurColumns: blurColumns,
        Cell: ({ value }) => {
          if (!blurColumns) {
            return value;
          }
          return (
            <div
              className="cursor-pointer"
              onClick={(e) => onBlurColumnClick(value)}
            >
              {value ?? "XXX"}
            </div>
          );
        },
      },
      {
        Header: "Revenue Multiple",
        accessor: "revenueMultiple",
        className: `text-right  w-[10%] pr-[2.5rem]`,
        headerspanClassName: "ml-auto ",
        headerClassName: "mr-auto",
        disableSortBy: blurColumns,
        blurColumns: blurColumns,
        Cell: ({ value }) => {
          if (!blurColumns) {
            return addCommasToNumber(value);
          }
          return (
            <div
              className="cursor-pointer"
              onClick={(e) => onBlurColumnClick(value)}
            >
              {value ?? "XXX"}
            </div>
          );
        },
      },
    ];
  }, []);

  const postMoneyValuationElement = (
    postMoneyValuationINR,
    postMoneyValuationUSD
  ) => {
    if (isDirectoryUser) {
      return Constants.restrictedPlaceholder;
    }
    if (postMoneyValuationINR === 0) {
      return "";
    }
    const postMoneyValuationINRAlt =
      postMoneyValuationINR < 100
        ? postMoneyValuationINR
        : Math.round(postMoneyValuationINR);
    return (
      <div>
        <span className="hidden">{postMoneyValuationINR}</span>
        <span>₹ {postMoneyValuationINRAlt.toLocaleString("en-IN")} Cr</span>
        <span className="pl-2 text-[#9d6c01] text-sm font-medium">
          {postMoneyValuationUSD}
        </span>
      </div>
    );
  };

  const dealDateElement = (investment) => {
    if (investment?.dates === Constants.restrictedPlaceholder) {
      return investment.dates;
    }

    return {
      date: investment.dates,
      id: investment.PEId,
      companyId: investment.PECompanyId,
    };
  };

  /* The above code is a JavaScript React code snippet. It defines a function that uses the `useMemo`
    hook to memoize the calculation of `rowData`. */
  const rowData = useMemo(() => {
    const rows =
      investmentsData?.map((investment) => {
        const dealDate = investment?.dates
          ? moment(investment.dates).format("MM-YYYY")
          : null;
        const postMoneyValuationINR =
          Number(investment?.Company_Valuation?.replace(/,/g, "")) || 0;
        const postMoneyValuationUSD = calculatePostMoneyUsd(
          postMoneyValuationINR,
          dealDate,
          investment?.fund_amount_inr,
          investment?.fund_amount_usd
        );

        return {
          company_name:
            investment?.company === Constants.restrictedPlaceholder
              ? investment.company
              : {
                  roundName: investment.company,
                  id: investment.PECompanyId,
                  restricted: investment.Revenue_Multiple,
                },
          dealDate: dealDateElement(investment),
          dealAmountInUS:
            investment.fund_amount_usd === "0.00" ||
            investment.fund_amount_usd === 0
              ? ""
              : investment.fund_amount_usd,
          dealAmountInCr:
            investment.fund_amount_inr === "0.00" ||
            investment.fund_amount_inr === 0
              ? ""
              : investment.fund_amount_inr,
          revenueMultiple: isDirectoryUser
            ? Constants.restrictedPlaceholder
            : investment.Revenue_Multiple === "0"
            ? ""
            : investment.Revenue_Multiple,
          postMoneyValuation: postMoneyValuationElement(
            postMoneyValuationINR,
            postMoneyValuationUSD
          ),
        };
      }) || [];

    return rows;
  }, [investmentsData]);

  /* The above code is using the `useMemo` hook in React to create a memoized value called
    `initialSort`. */
  const initialSort = useMemo(() => {
    if (investmentsData?.data?.isSort == false) {
      return [];
    } else {
      return [
        {
          id: "dealDate",
          desc: true,
        },
      ];
    }
  }, [investmentsData?.data?.isSort]);

  /* The above code is using the `useTable` hook from the React-Table library to create a table
    instance. */
  const tableInstance = useTable(
    {
      columns,
      data: rowData,
      disableSortRemove: true,
      initialState: {
        pageSize: Constants.noOfRowPerPage,
        pageIndex: 0,
        sortBy: initialSort,
      },
    },
    useSortBy,
    usePagination
  );

  /* The above code is using destructuring assignment to extract specific properties and methods from
    the `tableInstance` object. These extracted properties and methods are then used in a React
    component to render a table. */
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    page,
    prepareRow,
    gotoPage,
    state: { pageIndex, pageSize },
  } = tableInstance;

  const NodataComponent = () => {
    return (
      <div className="flex flex-col items-center justify-center h-20">
        <p className="px-5 text-[14px] mb-2">
          No Investments Found
        </p>
      </div>
    );
  };

  return (
    <div className="pb-8 space-y-6">
      <div className="">
        <div className="mx-4" ref={scrollRef}>
          <div className="bg-white border shadow-[0_2px_6px_0_rgba(0,0,0,0.08)] sm:rounded-lg">
            <div className="pt-4 px-5 border-b border-b-[#F1F1F1]">
                <div className="flex justify-between">
                    <p className=" text-[14pt] leading-[19pt] text-[#333333] lg:text-[17px] font-semibold mb-3 lg:leading-[22px]">
                        Portfolio Companies - Investments
                    </p>
                    {isTrialExport != "false" &&
                        isStudent == "false" &&
                        investmentsData?.length !== 0 && (
                            <ExcelDownloader
                                fileName={data?.profile?.fund_name?.replaceAll(/\s/g, "") ?? "Fund_Investments"}
                                data={{
                                    fund_id: data?.profile?.fund_id,
                                    type_id: 8,
                                    db_type: isVC ? "VC" : "PE"
                                }}
                                url={fundInvestmentsExport}
                            />
                    )}
                </div>
            </div>
            <>
                <div className="overflow-x-auto scrollbar relative">
                {rows?.length > 0 ? (
                    <table className=" datatable w-full" {...getTableProps()}>
                    <thead className="text-[12pt] leading-[15pt] bg-[#EDEDED] lg:text-[13px] lg:leading-[15px] text-[#666666] font-semibold py-4 px-0 text-center ">
                        {headerGroups.map((headerGroup) => (
                        <tr
                            key={headerGroup.id}
                            {...headerGroup.getHeaderGroupProps()}
                        >
                            {headerGroup.headers.map((column) => (
                            <th
                                {...column.getHeaderProps([
                                {
                                    className: `py-3.5 px-5 whitespace-nowrap ${column.headerClassName}`,
                                    style: column.headerStyle,
                                },
                                column.getSortByToggleProps(),
                                ])}
                                title=""
                            >
                                <div className="flex">
                                <span
                                    {...column.getHeaderProps([
                                    {
                                        className: `mr-1 ${column.headerspanClassName}`,
                                    },
                                    ])}
                                >
                                    {column.render("Header")}
                                </span>
                                {column.disableSortBy ? (
                                    ""
                                ) : column.isSorted ? (
                                    column.isSortedDesc ? (
                                    <SortAscendingIcon
                                        size={16}
                                        className="mt-[0.15rem] lg:mt-0"
                                    />
                                    ) : (
                                    <SortDescendingIcon
                                        size={16}
                                        className="mt-[0.15rem] lg:mt-0"
                                    />
                                    )
                                ) : (
                                    <SortIcon
                                    size={16}
                                    className="mt-[0.15rem] lg:mt-0"
                                    />
                                )}
                                </div>
                            </th>
                            ))}
                        </tr>
                        ))}
                    </thead>

                    <tbody
                        {...getTableBodyProps()}
                        className="relative text-[12pt] leading-[16pt] lg:text-[14px] font-sans_book_body text-left"
                    >
                        {page.map((row) => {
                        prepareRow(row);
                        return (
                            <tr {...row.getRowProps()} className="border-b">
                            {row.cells.map((cell) => {
                                if (
                                cell.column.blurColumns ||
                                cell.value ==
                                    Constants.restrictedPlaceholder
                                ) {
                                return (
                                    <td
                                    onClick={onBlurColumnClick}
                                    className=" relative whitespace-nowrap cursor-pointer"
                                    {...cell.getCellProps()}
                                    >
                                    <div className="blur-sm bg-[#DCDCDC] flex justify-center select-none w-full h-full px-5 py-4">
                                        {Constants.restrictedPlaceholder}
                                    </div>
                                    <div className="absolute h-full w-full flex items-center justify-center top-0 bottom-0">
                                        <LockIcon size={16} color="#696969" />
                                    </div>
                                    </td>
                                );
                                }

                                return (
                                <td
                                    colSpan={cell.column.colspan}
                                    rowSpan={cell.column.rowspan}
                                    {...cell.getCellProps([
                                    {
                                        className: `data-td-animate lg:whitespace-nowrap px-5 py-4 ${cell.column.className}`,
                                        style: cell.column.style,
                                    },
                                    ])}
                                >
                                    {cell.value ? cell.render("Cell") : " "}
                                </td>
                                );
                            })}
                            </tr>
                        );
                        })}
                    </tbody>
                    </table>
                ) : (
                    rows?.length <= 0 && <NodataComponent />
                )}
                </div>
                {rows?.length <= Constants.noOfRowPerPage ? (
                <></>
                ) : (
                <Paginate
                    currentPage={pageIndex + 1}
                    totalCount={rows?.length}
                    pageSize={pageSize}
                    onPageChange={(page) => {
                    gotoPage(page - 1);
                    scrollRef.current?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                        inline: "nearest",
                    });
                    }}
                />
                )}
                {<ConfirmPopup {...alert} onChange={onConfirmation} />}
            </>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FundInvestments;